import { ConfigProvider, Table } from 'antd'
import moment from 'moment'
import { useMediaQuery } from 'react-responsive'

import TitleAndSubtitle from 'components/title/TitleAndSubtitle'
import { TransactionStateBadge } from 'components/transactions/transaction-state-badge'
import { formatDurationWithTwoDigits } from 'helpers/duration'
import { formatEnergy } from 'helpers/number-format'
import { EmptyData } from '../../../../components/redesign/emptyData'
import { renderFormatMessage } from '../../../../helpers/intl'
import { Transaction, Transactionv2 } from '../../../../models/transaction'
import { styled } from '../../../../theme'
import TransactionOverviewCollapse from './transaction-overview-collapse'

export const secondsToTime = (secs: number) => {
  var hours = Math.floor(secs / (60 * 60))

  var divisor_for_minutes = secs % (60 * 60)
  var minutes = Math.floor(divisor_for_minutes / 60)

  var divisor_for_seconds = divisor_for_minutes % 60
  var seconds = Math.ceil(divisor_for_seconds)

  var obj = {
    h: hours,
    m: minutes,
    s: seconds,
  }
  return obj
}

const calculateDifference = (start: string, end: string) => {
  const connectedTime = moment(start, 'YYYY-MM-DD HH:mm:ss')
  const disconnectedTime = moment(end, 'YYYY-MM-DD HH:mm:ss')

  return disconnectedTime.diff(connectedTime)
}

let columns: any = [
  {
    title: renderFormatMessage('dashboard.transactionsPage.table.transactionId', 'ID'),
    key: 'id',
    dataIndex: 'id',
    sorter: (a: Transaction, b: Transaction) => a.id - b.id,
    align: 'left' as 'left',
  },
  {
    title: renderFormatMessage('dashboard.transactionsPage.table.userName', 'User Name'),
    sorter: (a: Transaction, b: Transaction) => a.user?.name.localeCompare(b.user?.name),
    render: (t: Transaction) => {
      return <TitleAndSubtitle transaction={t} />
    },
    align: 'left' as 'left',
  },
  {
    title: renderFormatMessage('dashboard.transactionsPage.table.duration', 'Duration'),
    sorter: (a: Transactionv2, b: Transactionv2) =>
      calculateDifference(a.connected, a.disconnected) - calculateDifference(b.connected, b.disconnected),
    render: (t: Transaction) => {
      const chargingDurationDNE = t.currentChargingDuration === null || t.currentChargingDuration === undefined
      const formattedDuration = formatDurationWithTwoDigits(t.currentChargingDuration)

      return chargingDurationDNE ? '-' : formattedDuration
    },
    align: 'left' as 'left',
  },
  {
    title: renderFormatMessage('dashboard.transactionsPage.table.totalEnergy', 'Total Energy'),
    sorter: (a: Transaction, b: Transaction) => {
      return a.energy - b.energy
    },
    render: (t: Transaction) =>
      t.energy === null || t.energy === undefined ? `-` : `${formatEnergy(t.energy / 1000)} kWh`,
    align: 'left' as 'left',
  },
  {
    title: renderFormatMessage('dashboard.transactionsPage.table.total', 'Total'),
    sorter: (a: Transaction, b: Transaction) => {
      return a.financial.total - b.financial.total
    },
    render: (t: Transaction) => `$${(t.financial.total / 100).toFixed(2)}`,
    align: 'left' as 'left',
  },
  {
    title: renderFormatMessage('dashboard.transactionsPage.table.state', 'State'),
    key: 'state',
    sorter: (a: Transaction, b: Transaction) => {
      return a.currentState.localeCompare(b.currentState)
    },
    render: (t: Transaction) => <TransactionStateBadge transaction={t} />,
    align: 'left' as 'left',
  },
]

export const HomePageTransactionOverviewContainer = styled.div`
  .website {
    background-color: #c8f9e8;
  }

  .mobile {
    background-color: #54dec7;
  }

  .rfid {
    background-color: #026e78;
    color: ${(props) => props.theme.colors.white} !important;
  }

  .cancelled {
    background-color: ${(props) => props.theme.colors.darkGray};
  }

  .roaming-partner {
    color: ${(props) => props.theme.colors.white};
    background-color: ${(props) => props.theme.colors.grey10};
  }

  .state {
    text-transform: capitalize;
    border-radius: 17px;
    padding: 4px 12px;
  }

  .state.confirmed {
    padding: 4px 12px;
  }

  .free,
  .confirmed,
  .paid,
  .completed,
  .checked_in {
    color: ${(props) => props.theme.colors.neonGreen};
  }

  .errored,
  .cancelled {
    color: ${(props) => props.theme.colors.dangerPrimary};
  }

  .pre-authorized,
  .pending_payment,
  .no-reservation {
    color: ${(props) => props.theme.colors.orangePeel};
  }

  .state.pre-authorized,
  .state.pending_payment,
  .state.no-reservation {
    background-color: ${(props) => props.theme.colors.orangePeel15};
  }

  .state.free,
  .state.confirmed,
  .state.paid,
  .state.completed,
  .state.checked_in {
    background-color: ${(props) => props.theme.colors.skyBlue15};
  }

  .state.errored,
  .state.cancelled {
    background-color: ${(props) => props.theme.colors.indianRed10};
  }

  .no-reservation {
    color: ${(props) => props.theme.colors.warningPrimary};
  }

  ${(props) => props.theme.breakpoints.down('xxl')} {
    .state.non-reservation {
      padding: 2px 6px;
    }

    .state.non-reservation.smallScreen {
      padding: 2px;
      font-size: 1rem;
    }
  }

  .isUndefined {
    background: rgba(255, 153, 0, 0.15);
    border-radius: 18px;
    width: 30px;
    height: 21px;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    color: ${(props) => props.theme.colors.orangePeel};
  }
`

interface props {
  transactions: Transaction[]
  transactionsLoading: boolean
}

export const HomePageTransactionOverview: React.FC<props> = ({ transactions, transactionsLoading }) => {
  const checkIfSmallScreen = useMediaQuery({ minWidth: 1200, maxWidth: 1250 })
  const makeResponsive = useMediaQuery({ minWidth: 0, maxWidth: 1144 })

  if (checkIfSmallScreen) {
    document.querySelector('.non-reservation')?.classList.add('smallScreen')
  } else {
    document.querySelector('.non-reservation')?.classList.remove('smallScreen')
  }

  return !makeResponsive ? (
    <HomePageTransactionOverviewContainer>
      <ConfigProvider renderEmpty={() => <EmptyData />}>
        <Table
          loading={transactionsLoading}
          className="transaction-overview"
          columns={columns}
          dataSource={transactions}
          pagination={false}
          rowKey="id"
        />
      </ConfigProvider>
    </HomePageTransactionOverviewContainer>
  ) : (
    <TransactionOverviewCollapse transactions={transactions} />
  )
}
